import {
    Alert,
    Box,
    Button,
    Paper,
    TextField,
    Typography,
} from '@mui/material';
import React, { useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';

import { localeFor, useI18n } from '../i18n';
import { BODY_GLYPHS } from '../tarot/astro/zodiac';

import { ChironFinding, chironOnDate } from './chironSigns';
import { fill, seoUi } from './copy';
import { chironSignPath } from './slugs';

const todayIso = (): string => {
    const d = new Date();
    const p = (n: number) => String(n).padStart(2, '0');
    return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}`;
};

/** Date of birth in, Chiron sign out. Needs no place or time, so it asks nothing else and shows no video. */
export const ChironCalculator: React.FC = () => {
    const { language } = useI18n();
    const ui = seoUi(language).calc;
    const astro = localeFor(language).astro;
    const [date, setDate] = useState('');
    const [found, setFound] = useState<ChironFinding | null>(null);
    const [outOfRange, setOutOfRange] = useState(false);

    const submit = () => {
        const result = date ? chironOnDate(date) : null;
        setFound(result);
        setOutOfRange(Boolean(date) && result === null);
    };

    return (
        <Paper
            component="section"
            aria-label={ui.title}
            sx={{ p: 3, my: 3, background: 'rgba(21,14,36,.92)' }}
        >
            <Typography variant="h6" component="h2" gutterBottom>
                {ui.title}
            </Typography>
            <Box
                component="form"
                noValidate
                onSubmit={(e: React.FormEvent) => {
                    e.preventDefault();
                    submit();
                }}
                sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}
            >
                <TextField
                    label={ui.date}
                    type="date"
                    value={date}
                    onChange={(e) => setDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: '1900-01-02', max: todayIso() }}
                />
                <Button type="submit" variant="contained" disabled={!date}>
                    {ui.button}
                </Button>
            </Box>
            {outOfRange && (
                <Alert severity="info" sx={{ mt: 2 }}>
                    {ui.range}
                </Alert>
            )}
            {found && (
                <Box sx={{ mt: 3 }} aria-live="polite">
                    <Typography color="text.secondary">{ui.result}</Typography>
                    <Typography
                        variant="h4"
                        component="p"
                        sx={{ fontFamily: 'Cinzel, serif' }}
                    >
                        {BODY_GLYPHS.chiron} {astro.signs[found.sign].name}{' '}
                        <Typography
                            component="span"
                            color="text.secondary"
                            variant="h6"
                        >
                            {Math.floor(found.degree)}°
                        </Typography>
                    </Typography>
                    {found.retrograde && (
                        <Typography sx={{ mt: 1 }}>
                            {ui.retrograde} {ui.retrogradeNote}
                        </Typography>
                    )}
                    {found.edge && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            {ui.edge}
                        </Alert>
                    )}
                    <Typography sx={{ mt: 2 }}>
                        {astro.inSign.chiron?.[found.sign]}
                    </Typography>
                    <Button
                        component={RouterLink}
                        to={chironSignPath(found.sign)}
                        sx={{ mt: 1 }}
                    >
                        {fill(ui.sign, { sign: astro.signs[found.sign].name })}
                    </Button>
                </Box>
            )}
        </Paper>
    );
};
